import React, { useEffect, useState, useMemo } from "react";
import { Box, Divider, TableRow, Tooltip } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import ScheduleNotes from "../ScheduleNotes";
import FullBreakdownPdf from "./Pdf/FullBreakdownPdf";
import PartialBreakdownPdf from "./Pdf/PartialBreakdownPdf";
import ScheduleHeader from "../../App/ScheduleHeader";
import ScheduleContainer from "../utils/ScheduleContainer";
import ScheduleContent, {
  BalanceVerification,
  StickyTotalRowStyle,
  StyledTableCell,
  StyledTypography,
} from "../utils/ScheduleContent";
import {
  TextFieldForName,
  TextFieldForViewOnly,
} from "../../utils/TextField/TextField";
import {
  detailsPLSchedules,
  investmentDetailsPLSchedules,
} from "../../../utils/static/detailsPL";
import {
  formatAmount,
  formatHistoryAmount,
} from "../../../utils/hooks/dataHandler";
import { CalculatorTextField } from "../../utils/TextField/NumericTextField";
import { StyledIconButton } from "../../utils/Icon";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  AdjustmentManagementDialog,
  LinkedRowSyncBanner,
  ScheduleTaggingDialog,
  collectDirtyLinkedSources,
  hydrateLinkedRows,
  markRowsSynced,
  useScheduleTagging,
} from "../../Common/TaxAdjustment";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import AdjustmentTaggingSelect, {
  createValidationRules,
} from "../../utils/AdjustmentTaggingSelect";
import BookIcon from "@mui/icons-material/Book";
import EditNoteIcon from "@mui/icons-material/EditNote";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BreakdownExcelUpload from "./utils/BreakdownExcelUpload";
import RemoveTaggingDialog from "../../Common/RemoveTaggingDialog";
import ChangeTaggingDialog from "../../Common/ChangeTaggingDialog";
import ComponentLoading from "../../App/ComponentLoading";
import ValidationWarningDialog from "../../utils/dialog/ValidationWarningDialog";

const BreakdownSchedule = ({
  breakdownType,
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  openScheduleWithDelay,
  canEdit = true,
}) => {
  const breakdownSection = `${breakdownType} Breakdown`;
  const { updateLinkedRow, updateLinkedRows } = useScheduleTagging();

  const [sourceData, setSourceData] = useState([]);
  const [grossBalance, setGrossBalance] = useState(0);
  const [adjustedTotalBalance, setAdjustedTotalBalance] = useState(0);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);

  const [schName, setSchName] = useState(`${breakdownType} Breakdown`);
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const handleSuccess = useSuccessDialog();
  const handleError = useErrorDialog();

  // Excel upload dialog state
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const handleOpenExcelUploadDialog = () => setExcelDialogOpen(true);
  const handleCloseExcelUploadDialog = () => setExcelDialogOpen(false);
  const [adjustmentManagementOpen, setAdjustmentManagementOpen] =
    useState(false);
  const [validationWarning, setValidationWarning] = useState({
    open: false,
    warnings: [],
    pendingSave: null,
    loading: false,
  });

  const breakdownSectionKeys = useMemo(
    () => [breakdownSection],
    [breakdownSection]
  );
  const rowsBySection = useMemo(
    () => ({
      [breakdownSection]: lines,
    }),
    [breakdownSection, lines]
  );

  const [taggingDialog, setTaggingDialog] = useState({
    open: false,
    isEdit: false,
    row: null,
    index: null,
  });
  const [removeDialog, setRemoveDialog] = useState({
    remove: false,
    index: null,
    value: null,
  });
  const onCloseTaggingDialog = () => {
    setTaggingDialog({
      open: false,
      isEdit: false,
      row: null,
      index: null,
    });
  };

  const onViewTaggedSchedule = (selectedRow) => {
    if (!selectedRow.taxAdjustment || !selectedRow.schedule) {
      handleError("There got an issue to view the schedule. Please try again.");
      return;
    }

    let taxAdjustment = selectedRow.taxAdjustment;

    const scheduleKeys =
      company.companyType === "investment"
        ? investmentDetailsPLSchedules
        : detailsPLSchedules;

    if (
      company.companyType === "investment" &&
      taxAdjustment.includes("Allocation of Expenses")
    ) {
      taxAdjustment = "Allocation Of Expenses";
    }

    openScheduleWithDelay(
      selectedRow.schedule,
      scheduleKeys[taxAdjustment],
      false
    );
  };

  const adjustBalance = (updatedRows) => {
    let amount = parseFloat(updatedRows["amount"]) || 0;
    let adjustment = updatedRows["taxAdjustment"];

    if (adjustment === "Deduct") return -Math.abs(amount);
    if (adjustment === "Added Back") return Math.abs(amount);
    if (adjustment === "FAAdditions") return Math.abs(amount);
    if (adjustment === "Donation Schedule") return Math.abs(amount);
    if (adjustment === "Renovation Schedule") return Math.abs(amount);

    if (
      [
        "Rental Schedule",
        "Other Income Schedule",
        "Foreign Trade / Branch Schedule",
      ].includes(adjustment) &&
      updatedRows["schedule"]
    ) {
      if (updatedRows["schedule"].adjustment === "Deduct")
        return -Math.abs(amount);
      if (updatedRows["schedule"].adjustment === "Added Back")
        return Math.abs(amount);
    }
    return 0; // If adjustment is "" or anything else, balance should be 0
  };

  const calculateAmountBalance = (rows) => {
    setGrossBalance(
      rows.reduce((sum, row) => {
        const amount = parseFloat(row.amount) || 0;
        return sum + amount;
      }, 0)
    );

    let newAdjustedBalance = rows.reduce((sum, row) => {
      return sum + adjustBalance(row);
    }, 0);

    setAdjustedTotalBalance(newAdjustedBalance);

    schedule.taxAdjustment = newAdjustedBalance < 0 ? "Deduct" : "Added Back";
  };

  const getTotals = (rows) => {
    const currentGrossBalance = rows.reduce((sum, row) => {
      const amount = parseFloat(row.amount) || 0;
      return sum + amount;
    }, 0);

    const currentAdjustedTotalBalance = rows.reduce(
      (sum, row) => sum + adjustBalance(row),
      0
    );

    return { currentGrossBalance, currentAdjustedTotalBalance };
  };

  const onDoneTagging = async (updatedRow) => {
    setLoading(true);
    const rowWithBalance = {
      ...updatedRow,
      balance: adjustBalance(updatedRow),
    };
    const latestLines = lines.map((row) =>
      row.row_id === updatedRow.row_id ? rowWithBalance : row
    );

    setLines(latestLines);
    calculateAmountBalance(latestLines);

    await handleSave("Tag applied to schedule successfully.", latestLines);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    if (!schedule || !scheduleDetails) return;
    //init
    if (schedule) {
      setSchName(schedule.name);
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (scheduleDetails) {
      setSourceData(scheduleDetails.sourceData);
      let temp_lines = hydrateLinkedRows([...scheduleDetails.rows]);
      temp_lines.forEach((line) => {
        if (!line.row_id) {
          line.row_id = uuidv4();
        }

        line["balance"] = adjustBalance(line);

        if (!line.historyAmount || line.historyAmount.length <= 0) {
          line.historyAmount = [line.amount];
        }
      });

      setLines(temp_lines);
      calculateAmountBalance(temp_lines);
    }
    setLoading(false);
  }, [schedule, scheduleDetails]);

  const handleInputChange = (value, { index, field }) => {
    setLines((prevLines) => {
      const updatedRows = [...prevLines];

      if (field === "amount") {
        updatedRows[index]["historyAmount"] = value;
        updatedRows[index][field] = value.reduce((sum, num) => sum + num, 0);
      } else {
        updatedRows[index][field] = value;
      }

      if (["amount"].includes(field)) {
        updatedRows[index].balance = adjustBalance(updatedRows[index]);
        calculateAmountBalance(updatedRows);
      }

      return updatedRows;
    });
  };

  const updateAdjustment = (index, value) => {
    setLines((prevLines) => {
      const updatedRows = [...prevLines];

      updatedRows[index].taxAdjustment = value;

      updatedRows[index].balance = adjustBalance(updatedRows[index]);
      calculateAmountBalance(updatedRows);

      return updatedRows;
    });
  };

  const dirtyLinkedSources = useMemo(
    () =>
      collectDirtyLinkedSources(
        { [breakdownSection]: lines },
        { sourceType: "pl_schedule", sourceId: schedule?.id }
      ),
    [breakdownSection, lines, schedule?.id]
  );
  const [syncingLinkedRows, setSyncingLinkedRows] = useState(false);

  const handleAdjustmentChange = (value, { index, field }) => {
    if (field !== "taxAdjustment") return;

    const row = lines[index];

    if (
      !row.taxAdjustment ||
      row.taxAdjustment === "" ||
      ["", "Added Back", "Deduct"].includes(row.taxAdjustment) ||
      !row.schedule
    ) {
      updateAdjustment(index, value);
      setRemoveDialog({
        tagging: false,
        remove: false,
        index: null,
        value: null,
      });
    } else {
      setRemoveDialog({
        tagging: true,
        remove: false,
        index,
        value,
      });
    }
  };

  const getValidationWarnings = (currentLines) => {
    if (breakdownType !== "Full") return [];

    const { currentGrossBalance } = getTotals(currentLines);
    const expectedTotal = Number(scheduleDetails?.totalSourceAmount) || 0;
    const difference = expectedTotal - currentGrossBalance;

    if (currentGrossBalance === expectedTotal) return [];

    return [
      {
        title: "Full breakdown total does not match the source amount",
        description: `Current: ${formatAmount(
          currentGrossBalance
        )}. Expected: ${formatAmount(expectedTotal)}. Difference: ${formatAmount(
          difference
        )}.`,
      },
    ];
  };

  const performSave = async (
    fallbackMsg = "The schedule has been saved successfully.",
    currentLines = lines
  ) => {
    schedule.name = schName;
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;

    const { currentGrossBalance, currentAdjustedTotalBalance } =
      getTotals(currentLines);
    schedule.taxAdjustment =
      currentAdjustedTotalBalance < 0 ? "Deduct" : "Added Back";

    await saveSchedule(
      schedule,
      {
        rows: currentLines.map((line) => {
          let formattedLine = Object.fromEntries(
            Object.entries(line).map(([key, obj]) => [key, obj ?? ""])
          );

          if (line.schedule) {
            formattedLine.taxAdjustment = {
              value: line.taxAdjustment ?? "",
              sch: line.schedule,
            };
          }

          return formattedLine;
        }),
        totalAdjustment: currentAdjustedTotalBalance,
        doc: JSON.stringify({
          sourceData,
          grossBalance: currentGrossBalance,
          adjustedTotalBalance: currentAdjustedTotalBalance,
          totalSourceAmount: scheduleDetails.totalSourceAmount,
          lines: currentLines,
          notes,
          reviewNotes,
        }),
      },
      fallbackMsg
    );
  };

  const handleSave = async (
    fallbackMsg = "The schedule has been saved successfully.",
    latestLines,
    options = {}
  ) => {
    const currentLines = latestLines != null ? [...latestLines] : [...lines];
    const warnings = options.skipValidationWarning
      ? []
      : getValidationWarnings(currentLines);

    if (warnings.length > 0) {
      setValidationWarning({
        open: true,
        warnings,
        pendingSave: { fallbackMsg, currentLines },
        loading: false,
      });
      return false;
    }

    await performSave(fallbackMsg, currentLines);
    return true;
  };

  const persistSyncedLines = async (rowIds, successMessage, currentLines = lines) => {
    const latestLines = markRowsSynced(currentLines, rowIds);
    setLines(latestLines);
    calculateAmountBalance(latestLines);
    await handleSave(successMessage, latestLines, {
      skipValidationWarning: true,
    });
  };

  const handleSyncLinkedRow = async (source) => {
    await updateLinkedRow(source, { showMessage: false });
    await persistSyncedLines(
      [source.row.row_id],
      "Linked schedule updated and saved."
    );
  };

  const handleSyncAllLinkedRows = async () => {
    if (!dirtyLinkedSources.length) return;
    setSyncingLinkedRows(true);
    try {
      await updateLinkedRows(dirtyLinkedSources, { showMessage: false });
      await persistSyncedLines(
        dirtyLinkedSources.map((source) => source.row.row_id),
        dirtyLinkedSources.length === 1
          ? "Linked schedule updated and saved."
          : `Linked schedules updated and saved for ${dirtyLinkedSources.length} rows.`
      );
    } finally {
      setSyncingLinkedRows(false);
    }
  };

  const handleConfirmValidationSave = async () => {
    const pendingSave = validationWarning.pendingSave;
    if (!pendingSave) return;

    try {
      setValidationWarning((prev) => ({ ...prev, loading: true }));
      await performSave(pendingSave.fallbackMsg, pendingSave.currentLines);
      setValidationWarning({
        open: false,
        warnings: [],
        pendingSave: null,
        loading: false,
      });
    } catch (error) {
      setValidationWarning((prev) => ({ ...prev, loading: false }));
      handleError(error);
    }
  };

  const addRow = (row) => {
    const isClickEvent =
      row instanceof MouseEvent || typeof row?.preventDefault === "function";

    const newRow =
      !isClickEvent && row
        ? {
            row_id: uuidv4(),
            name: row.description,
            amount: row.amount,
            historyAmount: [row.amount],
            taxAdjustment: "",
            reference: row.reference,
            balance: 0,
            permitToDelete: true,
          }
        : {
            row_id: uuidv4(),
            name: "",
            amount: 0,
            historyAmount: [],
            taxAdjustment: "",
            reference: "",
            balance: 0,
            permitToDelete: true,
          };

    setLines((prevRows) => {
      const updatedRows = [...prevRows, newRow];

      if (!isClickEvent && row) calculateAmountBalance(updatedRows);

      return updatedRows;
    });
  };

  const onExcelAddRows = (newRows) => {
    if (!Array.isArray(newRows) || newRows.length === 0) return;

    const rowsToAdd = newRows.map((row) => {
      const newRow = {
        row_id: uuidv4(),
        name: row.name || "",
        amount: row.amount || 0,
        historyAmount: row.amount ? [row.amount] : [],
        taxAdjustment: row.taxAdjustment || "",
        reference: row.reference || "",
        balance: 0,
        permitToDelete: true,
      };

      newRow.balance = adjustBalance(newRow);

      return newRow;
    });

    setLines((prevRows) => {
      const updatedRows = [...prevRows, ...rowsToAdd];

      calculateAmountBalance(updatedRows);

      return updatedRows;
    });

    handleCloseExcelUploadDialog();
  };

  const handleApplyManagedAdjustments = async (
    nextRowsBySection,
    appliedCount
  ) => {
    const updatedLines = (nextRowsBySection[breakdownSection] || []).map(
      (row) => ({
        ...row,
        balance: adjustBalance(row),
      })
    );

    setLines(updatedLines);
    calculateAmountBalance(updatedLines);

    await handleSave(
      `Applied adjustments to ${appliedCount} row(s).`,
      updatedLines
    );
  };

  const handleReorderLines = (updatedLines) => {
    setLines(updatedLines);
    calculateAmountBalance(updatedLines);
  };

  const removeRow = (index) => {
    let updatedRows = lines.filter((_, i) => i !== index);
    updatedRows.forEach((line) => {
      line["balance"] = adjustBalance(line);
    });
    setLines(updatedRows);
    calculateAmountBalance(updatedRows);

    return updatedRows;
  };

  const handleRemoveRow = (index) => {
    if (lines[index].schedule) {
      setRemoveDialog({
        tagging: false,
        remove: true,
        index: index,
      });
    } else {
      removeRow(index);
    }
  };

  return (
    <Box sx={{ padding: 4 }}>
      {loading ? (
        <ComponentLoading />
      ) : (
        <>
          <ScheduleHeader
            {...{
              title: schedule.type,
              companyName: company.name,
              uen: company.uen,
              yearOfAssessment: company.yearOfAssessment,
              financialYearStart: company.financialYearStart,
              financialYearEnd: company.financialYearEnd,
              handleSave,
              deleteSchedule,
              handleExcel: handleOpenExcelUploadDialog,
              handleManageAdjustments: () =>
                setAdjustmentManagementOpen(true),
              canEditSchedule: canEdit,
            }}
          >
            {breakdownType === "Full" && (
              <FullBreakdownPdf
                schedule={schedule}
                company={company}
                sourceData={sourceData}
                grossBalance={grossBalance}
                adjustedTotalBalance={adjustedTotalBalance}
                totalSourceAmount={scheduleDetails.totalSourceAmount}
                lines={lines}
                notes={notes}
                reviewNotes={reviewNotes}
              />
            )}
            {breakdownType === "Partial" && (
              <PartialBreakdownPdf
                schedule={schedule}
                company={company}
                grossBalance={grossBalance}
                adjustedTotalBalance={adjustedTotalBalance}
                lines={lines}
                notes={notes}
                reviewNotes={reviewNotes}
              />
            )}
          </ScheduleHeader>
          <br />

          <LinkedRowSyncBanner
            count={dirtyLinkedSources.length}
            onSyncAll={handleSyncAllLinkedRows}
            syncing={syncingLinkedRows}
            canEdit={canEdit}
            sourceLabel={`${breakdownType} Breakdown`}
          />

          <ScheduleContainer
            key={schedule.id}
            id={schedule.id}
            schedule={schedule}
            schName={schName}
            taxAdjustment={schedule.taxAdjustment}
            allowedToEditSchName={true}
            allowedToAdjustTax={false}
            isSourceApplicable
            sourceData={sourceData}
            onImport={(row) => addRow(row)}
            updateHeading={(newTitle) => {
              schedule.name = newTitle;
              setSchName(newTitle);
            }}
            canEditSchedule={canEdit}
          >
            {(fullScreenMode) => (
              <ScheduleContent
                fullScreenMode={fullScreenMode}
                tableTitle='Tax Breakdown Details'
                rows={lines}
                onAddRow={addRow}
                onSaveSchedule={handleSave}
                canEdit={canEdit}
                canReorderRows
                rowKey='row_id'
                onRowsReorder={handleReorderLines}
                columns={[
                  {
                    key: "name",
                    label: "Description",
                    headerAlign: "left",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row.name}
                        onChange={handleInputChange}
                        reference={{
                          index: index,
                          field: "name",
                        }}
                        disabled={row.status === "autoload"}
                        canEdit={canEdit}
                      />
                    ),
                  },
                  {
                    key: "taxAdjustment",
                    label: "Tax Adjustment",
                    headerAlign: "center",
                    colAlign: "center",
                    render: (row, index) => (
                      <AdjustmentTaggingSelect
                        linkedRowSource={{
                          row,
                          sourceType: "pl_schedule",
                          sourceId: schedule.id,
                          section: breakdownSection,
                          rowIndex: index,
                        }}
                        onSyncLinkedRow={handleSyncLinkedRow}
                        enableSearch
                        value={row.taxAdjustment}
                        onOpenTaggingDialog={() =>
                          setTaggingDialog({
                            open: true,
                            isEdit: false,
                            row: row,
                            index: index,
                          })
                        }
                        onChange={(e) =>
                          handleAdjustmentChange(e.target.value, {
                            index: index,
                            field: "taxAdjustment",
                          })
                        }
                        company={company}
                        placeholder='Select tax adjustment'
                        fullWidth
                        validationRules={[
                          createValidationRules.custom(
                            (value, context) => ({
                              isValid: !["", "Added Back", "Deduct"].includes(
                                context.taxAdjustment
                              )
                                ? Boolean(context.schedule)
                                : true,
                            }),
                            "This row needs to be tagged to a schedule. Click to tag."
                          ),
                        ]}
                        validationContext={row}
                        showValidationIcon={true}
                        sx={{
                          minWidth: 250,
                          "& .MuiSelect-select": {
                            fontWeight: 400,
                          },
                        }}
                        canEdit={canEdit}
                      />
                    ),
                  },
                  {
                    key: "amount",
                    label: "Amount",
                    headerAlign: "right",
                    colAlign: "right",
                    render: (row, index) => (
                      <Tooltip
                        title={
                          row?.historyAmount &&
                          row.historyAmount.length > 0 &&
                          formatHistoryAmount(row.historyAmount)
                        }
                        placement='top'
                      >
                        <Box display='inline-block' sx={{ width: "100%" }}>
                          <CalculatorTextField
                            value={row?.historyAmount || []}
                            onChange={handleInputChange}
                            reference={{
                              index: index,
                              field: "amount",
                            }}
                            fullWidth={true}
                            disabled={row.status === "autoload"}
                            canEdit={canEdit}
                          />
                        </Box>
                      </Tooltip>
                    ),
                  },
                  {
                    key: "balance",
                    label: "Balance",
                    headerAlign: "right",
                    colAlign: "right",
                    render: (row, _) => (
                      <TextFieldForViewOnly
                        value={formatAmount(row.balance || 0)}
                        sx={{ textAlign: "right" }}
                      />
                    ),
                  },
                  {
                    key: "reference",
                    label: "Reference",
                    headerAlign: "center",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row?.reference || ""}
                        onChange={handleInputChange}
                        reference={{
                          index: index,
                          field: "reference",
                        }}
                        disabled={row.status === "autoload"}
                        canEdit={canEdit}
                        viewOnlyAlign='center'
                      />
                    ),
                  },
                  {
                    key: "action",
                    label: "Action",
                    headerAlign: "center",
                    render: (row, index) => (
                      <>
                        {canEdit &&
                          !["", "Added Back", "Deduct"].includes(
                            row.taxAdjustment
                          ) &&
                          !row.schedule && (
                            <StyledIconButton
                              onClick={() =>
                                setTaggingDialog({
                                  open: true,
                                  isEdit: false,
                                  row: row,
                                  index: index,
                                })
                              }
                              icon={BookIcon}
                              tooltip='Tag Schedule'
                              iconColor='primary.main'
                            />
                          )}

                        {/* View Schedule */}
                        {!["", "Added Back", "Deduct"].includes(
                          row.taxAdjustment
                        ) &&
                          row.schedule && (
                            <StyledIconButton
                              icon={VisibilityIcon}
                              tooltip='View Schedule'
                              onClick={() => onViewTaggedSchedule(row)}
                              iconColor='primary.main'
                            />
                          )}

                        {/* Edit Schedule */}
                        {canEdit && row.schedule && (
                          <StyledIconButton
                            icon={EditNoteIcon}
                            onClick={() =>
                              setTaggingDialog({
                                open: true,
                                isEdit: true,
                                row: row,
                                index: index,
                              })
                            }
                            tooltip='Edit Schedule'
                            iconColor='warning.main'
                          />
                        )}

                        {canEdit && (
                          <StyledIconButton
                            onClick={() => handleRemoveRow(index)}
                            icon={DeleteIcon}
                            tooltip='Remove Row'
                            iconColor='error.main'
                          />
                        )}
                      </>
                    ),
                  },
                ]}
              >
                <TableRow sx={StickyTotalRowStyle}>
                  <StyledTableCell colSpan={canEdit ? 3 : 2}></StyledTableCell>
                  <StyledTableCell align='right'>
                    {breakdownType === "Full" && (
                      <BalanceVerification
                        grossBalance={grossBalance}
                        expectedTotal={scheduleDetails.totalSourceAmount}
                      />
                    )}
                    {breakdownType === "Partial" && (
                      <StyledTypography fontWeight={700}>
                        {formatAmount(grossBalance)}
                      </StyledTypography>
                    )}
                  </StyledTableCell>
                  <StyledTableCell align='right'>
                    <StyledTypography fontWeight={700}>
                      {formatAmount(adjustedTotalBalance)}
                    </StyledTypography>
                  </StyledTableCell>
                  <StyledTableCell colSpan={2}></StyledTableCell>
                </TableRow>
              </ScheduleContent>
            )}
          </ScheduleContainer>

          <Divider sx={{ marginY: 4 }} />
          <ScheduleNotes
            notes={notes}
            setNotes={setNotes}
            reviewNotes={reviewNotes}
            setReviewNotes={setReviewNotes}
            enableEdit={canEdit}
          />
        </>
      )}

      <ScheduleTaggingDialog
        companyType={company.companyType}
        open={taggingDialog.open}
        editMode={taggingDialog.isEdit}
        tcId={company.tc_id}
        detailsId={schedule.id}
        row={taggingDialog.row}
        rowIndex={taggingDialog.index}
        section={breakdownSection}
        reference='pl_schedule'
        source='pl_schedule'
        onDone={onDoneTagging}
        onClose={onCloseTaggingDialog}
        handleError={handleError}
      />

      <RemoveTaggingDialog
        open={removeDialog.remove}
        onClose={() => setRemoveDialog((prev) => ({ ...prev, remove: false }))}
        row={lines[removeDialog.index]}
        source='pl_schedule'
        onDone={async () => {
          try {
            const { index } = removeDialog;
            const updatedLines = removeRow(index);

            if (lines[index].schedule) {
              await handleSave(
                "Successfully removed the row and adjustment.",
                updatedLines
              );
            } else {
              handleSuccess("Successfully removed the row.");
            }
          } catch (error) {
            handleError("Failed to save the tax adjustment. Please try again.");
          }
        }}
        handleError={handleError}
      />

      <ChangeTaggingDialog
        open={removeDialog.tagging}
        row={lines[removeDialog.index]}
        source='pl_schedule'
        from={lines[removeDialog.index]?.taxAdjustment}
        to={removeDialog.value}
        onClose={() =>
          setRemoveDialog((prev) => ({ ...prev, tagging: false, value: null }))
        }
        onDone={async (updatedRow) => {
          try {
            const latestLines = lines.map((row) =>
              row.row_id === updatedRow.row_id
                ? { ...updatedRow, balance: adjustBalance(updatedRow) }
                : row
            );
            setLines(latestLines);
            calculateAmountBalance(latestLines);
            setRemoveDialog((prev) => ({
              ...prev,
              tagging: false,
              value: null,
            }));
            await handleSave("Successfully update the tax adjustment.", latestLines);
          } catch (error) {
            handleError(
              "Failed to save the tax adjustment. Your previous value is still intact. Please try again."
            );
          }
        }}
        handleError={handleError}
      />

      <BreakdownExcelUpload
        company={company}
        open={excelDialogOpen}
        handleCloseDialog={handleCloseExcelUploadDialog}
        onExcelAddRows={onExcelAddRows}
      />

      <ValidationWarningDialog
        open={validationWarning.open}
        title='Save with validation warning?'
        subtitle='Full breakdown amount check did not match.'
        warnings={validationWarning.warnings}
        loading={validationWarning.loading}
        onClose={() =>
          setValidationWarning({
            open: false,
            warnings: [],
            pendingSave: null,
            loading: false,
          })
        }
        onConfirm={handleConfirmValidationSave}
      />

      <AdjustmentManagementDialog
        open={adjustmentManagementOpen}
        onClose={() => setAdjustmentManagementOpen(false)}
        companyType={company.companyType}
        tcId={company.tc_id}
        detailsId={schedule.id}
        rowsBySection={rowsBySection}
        sectionKeys={breakdownSectionKeys}
        source='pl_schedule'
        reference='pl_schedule'
        rowLabel='breakdown rows'
        onApply={handleApplyManagedAdjustments}
        handleError={handleError}
      />
    </Box>
  );
};

export default BreakdownSchedule;
